import { useState, useEffect } from 'react';
import axios from 'axios';
import ContactImport from './ContactImport';
import { RefreshCw, Search } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function ContactList({ campaignId }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContacts();
  }, [campaignId, statusFilter, searchTerm]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams({
        limit: '50',
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(
        `${API_BASE}/dialer/campaigns/${campaignId}/contacts?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      called: 'bg-blue-100 text-blue-800',
      answered: 'bg-green-100 text-green-800',
      no_answer: 'bg-yellow-100 text-yellow-800',
      busy: 'bg-orange-100 text-orange-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded ${colors[status] || colors.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Contacts</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchContacts}
            className="p-2 hover:bg-gray-100 rounded"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <ContactImport campaignId={campaignId} onImportComplete={fetchContacts} />
        </div>
      </div>

      <div className="mb-4 space-y-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border rounded pl-8 pr-3 py-2 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full border rounded px-2 py-1 text-sm"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="called">Called</option>
          <option value="answered">Answered</option>
          <option value="no_answer">No Answer</option>
          <option value="busy">Busy</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-4">Loading...</div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No contacts found</p>
          <ContactImport campaignId={campaignId} onImportComplete={fetchContacts} />
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className="border rounded p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="font-semibold">{contact.full_name || 'Unknown'}</div>
                  {contact.company_name && (
                    <div className="text-sm text-gray-600">{contact.company_name}</div>
                  )}
                  {contact.title && (
                    <div className="text-xs text-gray-500">{contact.title}</div>
                  )}
                </div>
                {getStatusBadge(contact.status)}
              </div>
              <div className="text-sm text-gray-600">{contact.phone}</div>
              {contact.email && (
                <div className="text-xs text-gray-500">{contact.email}</div>
              )}
              {contact.call_count > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  Called {contact.call_count} time{contact.call_count !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

